<template>
  <div :class="['chat-message', `chat-message--${message.role}`]">
    <div class="chat-message__avatar">
      <v-avatar :color="message.role === AiAgentMessageRoleEnum.user ? 'primary' : 'secondary'" size="32">
        <v-icon small color="white">
          {{ message.role === AiAgentMessageRoleEnum.user ? 'mdi-account' : 'mdi-robot' }}
        </v-icon>
      </v-avatar>
    </div>

    <div class="chat-message__content">
      <div class="chat-message__header">
        <span class="chat-message__sender">
          {{ message.role === AiAgentMessageRoleEnum.user ? 'You' : 'AI Assistant' }}
        </span>
        <span class="chat-message__time">
          {{ formatTimePassed(message.timestamp) }}
        </span>
      </div>

      <div class="chat-message__text" :class="{ 'chat-message__text--error': message.isError }">
        {{ message.content }}
      </div>

      <div v-if="hasRecordData" class="chat-message__records">
        <RecordCardRenderer
          :record-type="message.data.record_type"
          :items="message.data.items.result || []"
        />
      </div>

      <div v-if="message.executionTimeMs" class="chat-message__meta">
        <v-chip x-small outlined>
          <v-icon x-small left>mdi-clock-outline</v-icon>
          {{ message.executionTimeMs }}ms
        </v-chip>
        <v-chip
          v-if="message.intent"
          x-small
          outlined
          class="ml-2"
        >
          <v-icon x-small left>mdi-tag-outline</v-icon>
          {{ message.intent }}
        </v-chip>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import Vue from 'vue';

import { AiAgentMessageRoleEnum, IAiMessage } from '@/bundles/AiAgent/types';
import { formatTimePassed } from '@/utils/filters';
import { isValuePresent } from '@/bundles/App/helpers/isValuePresent';

import RecordCardRenderer from '@/bundles/AiAgent/components/cards/RecordCardRenderer.vue';

interface IAiChatMessageInstance {
  AiAgentMessageRoleEnum?: typeof AiAgentMessageRoleEnum;
}

export default Vue.extend({
  name: 'AiChatMessage',

  components: {
    RecordCardRenderer,
  },

  props: {
    message: {
      type: Object as () => IAiMessage,
      required: true,
    },
  },

  data: (): IAiChatMessageInstance => ({}),

  computed: {
    hasRecordData (): boolean {
      return isValuePresent(this.message.data?.items?.result);
    },
  },

  created: function () {
    this.AiAgentMessageRoleEnum = AiAgentMessageRoleEnum;
  },

  methods: {
    formatTimePassed,
  },
});
</script>

<style lang="scss" scoped>
.chat-message {
  display: flex;
  gap: 12px;
  padding: 12px 16px;

  &--user {
    flex-direction: row-reverse;

    .chat-message__content {
      align-items: flex-end;
    }

    .chat-message__text {
      background-color: #1976d2;
      color: white;
    }
  }

  &--assistant {
    .chat-message__text {
      background-color: #f5f5f5;
      color: #333;

      &--error {
        background-color: #ffe6e6;
      }
    }
  }

  &__avatar {
    flex-shrink: 0;
  }

  &__content {
    display: flex;
    flex-direction: column;
    gap: 4px;
    max-width: 70%;
    min-width: 0;
  }

  &__header {
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 12px;
  }

  &__sender {
    font-weight: 600;
    color: #666;
  }

  &__time {
    color: #999;
    font-size: 11px;
  }

  &__text {
    padding: 10px 14px;
    border-radius: 12px;
    word-wrap: break-word;
    line-height: 1.5;

    ::v-deep code {
      background-color: rgba(0, 0, 0, 0.1);
      padding: 2px 6px;
      border-radius: 4px;
      font-family: 'Courier New', monospace;
      font-size: 0.9em;
    }

    ::v-deep ul {
      margin: 8px 0;
      padding-left: 20px;
    }

    ::v-deep li {
      margin: 4px 0;
    }

    ::v-deep strong {
      font-weight: 600;
    }

    ::v-deep a {
      color: #1976d2;
      text-decoration: none;
      font-weight: 600;
      border-bottom: 1px solid #1976d2;

      &:hover {
        color: #1565c0;
        border-bottom-color: #1565c0;
      }
    }
  }

  &__records {
    margin-top: 8px;
    width: 100%;
  }

  &__meta {
    display: flex;
    gap: 4px;
    margin-top: 4px;
  }
}
</style>
